import { action, computed, observable } from 'mobx';

import { Throttle } from '~api/utils/utils';
import { removeKitItem } from '~api/KitsAPI/KitsAPI';
import { removeOfferItem } from '~api/OffersAPI/OffersApi';
import { IRemoveKitPayload } from '~api/KitsAPI/IKitsAPI';
import { IRemovePayload, IUpdatePayload, IFetchCartParams } from '~api/CartAPI/ICartApiSdk';
import { fetchCartItems, updateCartItem } from '~api/CartAPI/CartAPI';
import { fetchRestAPICartItems, updateRestAPICartItem } from '~api/CartRestAPI/CartRestAPI';
import {
    fetchVulcanCart,
    fetchVulcanCartWithSchema,
    isCartVulcanAPI
} from '~api/CartVulcanAPI/CartVulcanAPI';

import Cart from '~domain/entities/Cart/Cart';
import { ICartProps } from '~domain/entities/Cart/ICart';
import CartSidebar from '~domain/entities/CartSidebar/CartSidebar';
import { ICartRepositoryProps } from '~repositories/CartRepository/ICartRepository';
import { isCartRestAPI } from '~api/CartRestAPI/utils/utils';

export default class CartRepository {
    @observable public cart: Cart;
    @observable public cartSidebar: CartSidebar;

    @observable private _abortRequest: boolean;
    @observable private _abortController: AbortController;

    @observable public isLoading: boolean = false;
    @observable public error: unknown = null;
    @observable public fetched: boolean = false;
    @observable public signedInUser: boolean;
    @observable public isCartTransLimitReached: boolean = false;

    private isCartRestAPI = isCartRestAPI();
    private fetchCartParams?: IFetchCartParams;

    private cartRequest = new Throttle(async () => {
        const cartData = await this.getCartData(this.fetchCartParams);

        this.setCart(cartData);
    });

    private getCartData = async (params?: IFetchCartParams) => {
        if (isCartVulcanAPI() === 'enabled:schema') {
            return fetchVulcanCartWithSchema();
        } else if (isCartVulcanAPI() === 'enabled') {
            return fetchVulcanCart();
        }

        if (this.isCartRestAPI) {
            return fetchRestAPICartItems(this._abortController.signal, params);
        }

        return fetchCartItems();
    };

    constructor({ cart, cartSidebar }: ICartRepositoryProps) {
        this._abortRequest = false;
        this._abortController = new AbortController();

        this.cart = cart;
        this.cartSidebar = cartSidebar;
    }

    @computed
    public get abortRequest() {
        return this._abortRequest;
    }
    public set abortRequest(signal: boolean) {
        this._abortRequest = signal;
    }

    @computed
    public get abortController() {
        return this._abortController;
    }

    public fetchCart = async (params?: IFetchCartParams) => {
        this.isLoading = true;
        this.error = null;
        this.fetched = false;
        this.fetchCartParams = params;

        try {
            await this.cartRequest.execute();
        } catch (error) {
            this.error = error;
        } finally {
            this.isLoading = false;
            this.fetched = true;
        }
    };

    public removeItem = async (props: IRemovePayload) => {
        const { skus, collectionId, cartId } = props;
        const isCollection = !!collectionId && !!cartId;
        const itemToRemove = {
            skus,
            cartId,
            quantity: 0
        };
        const payload = isCollection ? { ...itemToRemove, collectionId, cartId } : itemToRemove;

        this.isCartRestAPI ? await updateRestAPICartItem(payload) : await updateCartItem(payload);
        this.fetchCart();
    };

    public removeOfferItem = async (offerCode: string) => {
        await removeOfferItem(offerCode);
        this.fetchCart();
    };

    public removeKitItem = async (payload: IRemoveKitPayload) => {
        await removeKitItem(payload);
        this.fetchCart();
    };

    public updateItem = async ({ skus, quantity, cartId }: IUpdatePayload) => {
        this.isCartRestAPI
            ? await updateRestAPICartItem({ skus, quantity, cartId })
            : await updateCartItem({ skus, quantity });
        this.fetchCart();
    };

    @action
    public setCart = (cartItems: ICartProps): void => {
        this.cart = Cart.fromJSON(cartItems);
    };

    @action
    public setCartTransLimitReached = (status: boolean): void => {
        this.isCartTransLimitReached = status;
    };

    @action
    public setSignedInUser = (value: boolean): void => {
        this.signedInUser = value;
    };

    @computed public get isCartFull() {
        return this.cart.isFull;
    }

    @computed public get isCartQtyLimitReached() {
        return this.cart.isCartQtyLimitReached;
    }

    @computed public get maxTotalItemLimit() {
        return this.cart.maxTotalItemLimit;
    }

    @computed
    public get isCartDiscounted(): boolean {
        return this.cart.cartTotals.discount.isDiscounted;
    }
}
